import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {combineLatest, firstValueFrom, from} from 'rxjs';
import { map } from 'rxjs/operators';

import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { Role, RoleApiService } from '@sim/role';

import { SwalService } from '@atmi/core';

import { FiltersComponent } from '@sim/users/components/filters/filters.component';
import { UsersFilter, UsersFiltersMeta } from '@sim/users/models/filter';
import { User } from '@sim/users/models/user';
import { UsersApiService } from '@sim/users/services/users-api.service';


type UserRole = User & { roleConcrete?: Role };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-users',
  styleUrls: ['./users.component.scss'],
  templateUrl: './users.component.html',
})
export class UsersComponent extends BaseTableFilteredComponent<UserRole, UsersFilter> implements UiTableComponent<UserRole> {

  readonly MobileFiltersComponent = FiltersComponent;

  readonly fields: TableField<UserRole>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'checkbox',
      selectable: true,
    },
    {
      header: this.translate.instant('Username'),
      key: 'username',
    },
    {
      header: this.translate.instant('Email'),
      key: 'email',
    },
    {
      header: this.translate.instant('Nome'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'firstName',
    },
    {
      header: this.translate.instant('Cognome'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'lastName',
    },
    {
      context: (u) => u.roleConcrete && u.roleConcrete.name,
      header: this.translate.instant('Ruoli'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'role',
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) => combineLatest([
    this.userApi.index({ page, limit, sort, filter }),
    this.roleApi.index(),
  ]).pipe(
    map(([userPage, rolePage]) => {
      const roles = rolePage.data;
      const users: UserRole[] = userPage.data.map((u) => ({
        ...u,
        roleConcrete: roles.find((r) => r.id === u.role),
      }));

      return { count: userPage.count, data: users };
    }),
  ));

  readonly contextMenus: Array<TableContextMenu<User>> = [
    {
      action: (item) => this.router.navigate(['/users/manager', item.id], { queryParamsHandling: 'merge' }),
      label: this.translate.instant('Modifica'),
    },
    {
      action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
      label: this.translate.instant('Elimina'),
    },
  ];

  constructor(
    protected readonly translate: TranslateService,
    protected readonly router: Router,
    protected readonly route: ActivatedRoute,
    protected readonly swalService: SwalService,
    private readonly userApi: UsersApiService,
    private readonly roleApi: RoleApiService, // TODO: Maybe too linked with this module
  ) {
    super(translate, router, route, UsersFiltersMeta, swalService, '/users/manager');

    this.fields.push(basicEditTemplateOptions);
    this.fields.push(basicDeleteTemplateOptions);
  }

  deleteConcreteHandler(id: string) {
    return this.userApi.remove(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = ids.map((id) => firstValueFrom(this.userApi.remove(id)));

    return from(Promise.all(deleteApis));
  }
}
